import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { NotifyParty } from "../../../../../../../models/notify.party";
import { Filters, Searches, Sort, Sorts } from "../../../../../../../models/shipeezi.table";
import NotifyPartyService from "../../../../../../../services/notify.party.service";

export const useActions = () => {
  const { currentUser } = useAppState();

  const [notifyPartyList, setNotifyPartyList] = useState<NotifyParty[]>([]);
  const [notifyParty, setNotifyParty] = useState<NotifyParty>();
  const [hasErrors, setHasErrors] = useState<boolean>();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(15);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const { refetch, isFetching } = useQuery(
    ["notify.party.list", page, pageSize, filters, searches, sorting],
    () =>
      NotifyPartyService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        include: JSON.stringify([{ association: "location" }]),
        sortBy: sorting ? JSON.stringify(sorting) : JSON.stringify([["createdAt", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setNotifyPartyList(response.data);
      },
    }
  );

  const { refetch: getNotifyPartyTotal } = useQuery(
    ["notify.party.total", filters, searches],
    () =>
      NotifyPartyService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
      },
    }
  );

  const { mutate: createNotifyParty, isLoading: isCreating } = useMutation(
    ["notify.party.create"],
    (notifyParty: NotifyParty) => NotifyPartyService.create(notifyParty),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getNotifyPartyTotal();
      },
      onError: (err: any) => {
        NotificationToast.error(err.response.data);
        setHasErrors(true);
      },
    }
  );

  const { mutate: updateNotifyParty, isLoading: isUpdating } = useMutation(
    ["notify.party.update"],
    ({ id, notifyParty }: { id: number; notifyParty: NotifyParty }) =>
      NotifyPartyService.update(id, notifyParty),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getNotifyPartyTotal();
      },
      onError: (err: any) => {
        NotificationToast.error(err.response.data);
        setHasErrors(true);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    variables: {
      notifyPartyList,
      notifyParty,
      hasErrors,
      page,
      pageSize,
      total,
      isFetching,
      isCreating,
      isUpdating,
    },
    setPageSize,
    setNotifyParty,
    createNotifyParty,
    updateNotifyParty,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
  };
};
